import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { ConsentConfiguration } from '@luckycart/xpn-types';

import { BlockInfoType } from '../../common-types/BlockInfo.type';
import { CheckboxCollectOptin, CollectOptin, CollectSmartOptin, ConsentsContainer, Toggle } from './Consents.style';

export interface Props {
  suOptinForm?: boolean;
  consentsConfig: ConsentConfiguration[];
  addEditableBlock?: (block: BlockInfoType) => void;
  isEdition?: boolean;
  onBlockEdit?: React.MouseEventHandler<HTMLDivElement>;
  displayName: string;
  dataTemplateConfig: string;
  testId?: string;
  disabled?: boolean;
  checked?: React.ChangeEventHandler<HTMLInputElement>;
  onOptinsKeysAcceptedChange: (newOptinsKeysAccepted: string[]) => void;
}

export const Consents: React.FunctionComponent<Props> = ({
  suOptinForm,
  consentsConfig,
  addEditableBlock,
  isEdition,
  onBlockEdit,
  displayName,
  dataTemplateConfig,
  testId,
  disabled,
  checked,
  onOptinsKeysAcceptedChange,
} : Props) => {
  const dataTemplateType = 'consents';
  const [optinsKeysAccepted, setOptinsKeysAccepted] = useState<string[]>([]);

  const computedConsents = useMemo(() => {
    return consentsConfig
      .sort(({ position: positionA }, { position: positionB }) => positionA - positionB)
      .filter(({ activated }) => activated);
  }, [consentsConfig]);

  const handleToggle = (key: string, event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      setOptinsKeysAccepted((prevOptinsKeysAccepted) => [...prevOptinsKeysAccepted, key]);
      onOptinsKeysAcceptedChange([...optinsKeysAccepted, key]);
    } else {
      setOptinsKeysAccepted(optinsKeysAccepted.filter((optin) => optin !== key));
      onOptinsKeysAcceptedChange(optinsKeysAccepted.filter((optin) => optin !== key));
    }
  };

  const isOptinListContains = (key: string): boolean => {
    return computedConsents.some((consent) => consent.key === key);
  };

  const shouldToggleBeDisplayed = (key: string): string => {
    if (isOptinListContains(key)) {
      return 'flex';
    }
    return 'none';
  };

  useEffect(
    () => {
      if (addEditableBlock) {
        addEditableBlock({
          blockType: dataTemplateType,
          configPath: dataTemplateConfig,
          label: displayName,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataTemplateConfig]
  );

  return (
    <ConsentsContainer
      suOptinForm={suOptinForm}
      computedConsents={computedConsents}
      disabled={disabled}
      isEdition={isEdition}
      data-template-type={dataTemplateType}
      data-display-name={displayName}
      data-config-path={dataTemplateConfig}
      data-testid={testId}
      onClick={onBlockEdit}
    >
      {computedConsents.map(({ type, labelHtml, key }) => (
        <>
          {
            type === 'text' && (
              suOptinForm === true ? <CollectSmartOptin key={key} dangerouslySetInnerHTML={{ __html: labelHtml }} /> :
                <CollectOptin key={key} dangerouslySetInnerHTML={{ __html: labelHtml }} />
            )
          }
          {
            type === 'checkbox' && (
              <CheckboxCollectOptin key={key} onChange={checked}>
                <div>
                  <input type="checkbox" name="consentsOptinCheckbox" />
                </div>
                <label htmlFor="consentsOptinCheckbox" dangerouslySetInnerHTML={{ __html: labelHtml }} />
              </CheckboxCollectOptin>
            )
          }
          {/* Currently: If toggle -> LECLERC new optin screen
          This code add a label and a toggle for Profilage then another label and 2 toggles for Mail and SMS if present
          From config, optins list should be in this order : 1.profilage, 2.mail, 3.sms */}
          {
            type === 'toggle' && (
              // Display Profilage Toggle if exist
              <>
                {key === 'profilage' ? (
                  <Toggle>
                    <div>
                      <label htmlFor="consentsOptinToggle" dangerouslySetInnerHTML={{ __html: labelHtml }} />
                      <p>
                        <input type="checkbox" name="consentsOptinToggle" onChange={(event): void => handleToggle(key, event)} />
                        <span className='toggleLabel'>Offres personnalisées</span>
                      </p>
                    </div>
                  </Toggle>
                ): (
                  <>
                    {
                      (isOptinListContains('mail') && isOptinListContains('sms') && key === 'sms') || 
                      (isOptinListContains('mail') && isOptinListContains('sms') && isOptinListContains('courrier') && key === 'courrier') 
                        ? null 
                        : (
                          <Toggle>
                            <div>
                              <label htmlFor="consentsOptinToggle" dangerouslySetInnerHTML={{ __html: labelHtml }} />
                              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                <div style={{ display: shouldToggleBeDisplayed('mail'), width: '46vw', margin: '2vh 0' }}>
                                  <input type="checkbox" name="consentsOptinToggle" onChange={(event): void => handleToggle('mail', event)} />
                                  <span className='toggleLabel'>Par Email</span>
                                </div>
                                <div style={{ display: shouldToggleBeDisplayed('sms'), width: '46vw', margin: '2vh 0' }}>
                                  <input type="checkbox" name="consentsOptinToggle" onChange={(event): void => handleToggle('sms', event)} />
                                  <span className='toggleLabel'>Par SMS</span>
                                </div>
                                <div style={{ display: shouldToggleBeDisplayed('courrier'), width: '46vw', margin: '2vh 0' }}>
                                  <input type="checkbox" name="consentsOptinToggle" onChange={(event): void => handleToggle('courrier', event)} />
                                  <span className='toggleLabel'>Par voie postale</span>
                                </div>
                              </div>
                            </div>
                          </Toggle>
                        )
                    }
                  </>
                )}
              </>
            )
          }
        </>
      ))}
    </ConsentsContainer>
  );
};
